import { useRef, type ReactElement, Children } from 'react';
import type {
  GroupBase,
  OptionProps,
  GroupProps as ReactSelectGroupProps,
} from 'react-select';
import { components } from 'react-select';

import {
  applyStoredSelectionSortingOptions,
  sortSelectedOptions,
} from './sort-selected-options-first.js';
import type { BaseGroup, BaseOption } from '../types.js';

export type GroupProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectGroupProps<OptionValue, isMulti, Group>;

export function Group<
  OptionValue,
  isMulti extends boolean,
  Group extends BaseGroup<OptionValue>,
>(props: Readonly<GroupProps<BaseOption<OptionValue>, isMulti, Group>>) {
  const initialRender = useRef(true);
  const sortingMap = useRef(new WeakMap());

  const { children, innerProps, ...remainingProps } = props;
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props.data;

  let childrenArray = Children.toArray(children) as Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  >;

  // During the initial rendering, the sorting is created and positions saved in a WeakMap.
  if (props.selectProps.showSelectedOptionsFirst) {
    if (initialRender.current === true) {
      [childrenArray, sortingMap.current] = sortSelectedOptions(childrenArray);
    }
    // Otherwise the data is sorted by the initial sorting parameters.
    else {
      const [newChildrenArray, newSortingMap] =
        applyStoredSelectionSortingOptions(childrenArray, sortingMap.current);
      childrenArray = newChildrenArray;
      if (newSortingMap) {
        sortingMap.current = newSortingMap;
      }
    }
  }

  initialRender.current = false;

  return (
    <components.Group
      {...remainingProps}
      // Need to Object.assign to allow for adding the data attribute.
      innerProps={Object.assign(innerProps ?? {}, {
        role: 'group',
        className: consumerClassName,
        style: consumerStyle,
        'data-testid': dataTestId,
      })}
    >
      {childrenArray}
    </components.Group>
  );
}
